import { useApp, useTick } from "@pixi/react"
import type { DisplayObject } from "pixi.js"
import { useCallback, useEffect, useMemo, useRef } from "react"
import { noop } from "@/shared/utils/noop"
import { selectSetPanPosition, selectSetZoomLevel } from "../store/selectors"
import { useGraphStore } from "../store/store"

const DEFAULT_PAN_SPEED = 0.2
const PAN_TO_NODE_SPEED = 0.05

function interpolate(current: number, target: number, easing: number): number {
  return current + (target - current) * easing
}

interface UsePanAndZoomOptions {
  containerName: string
  wrapperRef: React.RefObject<HTMLDivElement>
  initialZoom?: number
  disabled?: boolean
  useStore?: boolean
}

/**
 * A hook that provides pan and zoom functionality for a PIXI.js container element.
 *
 * @param options - Configuration options for pan and zoom functionality
 * @param options.containerName - The name attribute in the PIXI.js container to apply pan and zoom functionality.
 * @param options.wrapperRef - React ref for the HTML wrapper element for hit area and cursor adjustments.
 * @param options.initialZoom - (Optional) The initial zoom value (default: 1). Also updates when changed.
 * @param options.disabled - (Optional) Disables pan/zoom functionality if true (default: false).
 * @param options.useStore - (Optional) Use Zustand store for state management instead of local refs (default: false).
 */
export function usePanAndZoom({
  containerName,
  wrapperRef,
  initialZoom = 1,
  disabled = false,
  useStore = false
}: UsePanAndZoomOptions) {
  const app = useApp()
  const containerRef = useRef<DisplayObject | null>(null)

  const initialStorePan = useMemo(() => (useStore ? useGraphStore.getState().panPosition : { x: 0, y: 0 }), [useStore])
  const initialStoreZoom = useMemo(
    () => (useStore ? useGraphStore.getState().zoomLevel : initialZoom),
    [useStore, initialZoom]
  )

  // Store action functions
  const setStorePanPosition = useGraphStore(useStore ? selectSetPanPosition : () => noop)
  const setStoreZoomLevel = useGraphStore(useStore ? selectSetZoomLevel : () => noop)

  const resetEasing = useCallback(() => {
    easingRef.current = DEFAULT_PAN_SPEED
  }, [])

  useEffect(() => {
    containerRef.current = app.stage.getChildByName(containerName)
  }, [app, containerName])

  // Refs for target/current position and scale - initialize from store once
  const targetPosition = useRef(initialStorePan)
  const currentPosition = useRef(initialStorePan)
  const targetScale = useRef(initialStoreZoom)
  const currentScale = useRef(initialStoreZoom)

  // Refs for panning momentum and state.
  const velocityRef = useRef({ x: 0, y: 0 })
  const isPanningRef = useRef(false)
  const easingRef = useRef(DEFAULT_PAN_SPEED)

  // Track if the user has zoomed.
  const hasZoomed = useRef(false)

  // Track if animation is needed.
  const shouldAnimateRef = useRef(false)

  // Scale constraints.
  const minScale = 0.5
  const maxScale = 3

  useEffect(() => {
    if (!hasZoomed.current && containerRef.current) {
      const zoom = useStore ? initialStoreZoom : initialZoom
      const panX = useStore ? initialStorePan.x : 0
      const panY = useStore ? initialStorePan.y : 0

      targetScale.current = zoom
      currentScale.current = zoom
      targetPosition.current = { x: panX, y: panY }
      currentPosition.current = { x: panX, y: panY }

      containerRef.current.scale.set(zoom)
      containerRef.current.position.set(panX, panY)

      if (useStore) {
        hasZoomed.current = true
      }
    }
  }, [initialZoom, useStore, initialStoreZoom, initialStorePan])

  useEffect(() => {
    const wrapperElement = wrapperRef.current

    if (!wrapperElement || disabled || !containerRef.current) return

    // Enable container interactivity
    containerRef.current.eventMode = "static"

    // AbortController for cleaning up event listeners.
    const abortController = new AbortController()
    const { signal } = abortController

    // Track the last mouse position for panning
    let lastMousePos = { x: 0, y: 0 }

    const isFromPanBlockedTarget = (nativeEventTarget: EventTarget | null): boolean => {
      const element = nativeEventTarget as HTMLElement | null
      if (!element) return false
      return Boolean(element.closest('[data-block-pan="true"]'))
    }

    const onPointerDown = (e: PointerEvent) => {
      if (isFromPanBlockedTarget(e.target)) return
      isPanningRef.current = true
      hasZoomed.current = true
      shouldAnimateRef.current = true
      resetEasing()

      const rect = wrapperElement.getBoundingClientRect()

      lastMousePos = { x: e.clientX - rect.left, y: e.clientY - rect.top } // Calculate local coordinates
      velocityRef.current = { x: 0, y: 0 } // Reset velocity
      targetPosition.current = { ...currentPosition.current } // Sync target position

      const targetEl = e.target as HTMLElement
      targetEl.setPointerCapture(e.pointerId) // Capture pointer events
    }

    const onPointerMove = (e: PointerEvent) => {
      if (!isPanningRef.current) return

      const rect = wrapperElement.getBoundingClientRect()
      const local = { x: e.clientX - rect.left, y: e.clientY - rect.top } // Calculate local coordinates
      const dx = local.x - lastMousePos.x // Calculate delta x
      const dy = local.y - lastMousePos.y // Calculate delta y

      lastMousePos = { ...local } // Update last mouse position
      targetPosition.current.x += dx // Update target position
      targetPosition.current.y += dy
      velocityRef.current = { x: dx, y: dy } // Update velocity
    }

    const onPointerUp = (e: PointerEvent) => {
      isPanningRef.current = false

      const targetEl = e.target as HTMLElement

      targetEl.releasePointerCapture(e.pointerId) // Release pointer events
    }

    // Attach pointer events to the wrapper element.
    wrapperElement.addEventListener("pointerdown", onPointerDown, { signal })
    wrapperElement.addEventListener("pointermove", onPointerMove, { signal })
    wrapperElement.addEventListener("pointerup", onPointerUp, { signal })
    wrapperElement.addEventListener("pointerleave", onPointerUp, { signal })

    const onWheel = (e: WheelEvent) => {
      if (isFromPanBlockedTarget(e.target)) return
      e.preventDefault()
      shouldAnimateRef.current = true
      resetEasing()

      const rect = wrapperElement.getBoundingClientRect()
      const pointerX = e.clientX - rect.left // Calculate pointer x relative to wrapper
      const pointerY = e.clientY - rect.top // Calculate pointer y relative to wrapper

      const oldScale = targetScale.current // Current scale
      const sensitivity = 0.001 // Zoom sensitivity
      const newScale = oldScale * Math.exp(-sensitivity * e.deltaY) // Calculate new scale
      const roundedNewScale = Math.round(newScale * 1000) / 1000 // Round to 3 decimal places
      const clampedNewScale = Math.max(minScale, Math.min(maxScale, roundedNewScale)) // Clamp scale to constraints

      // Calculate world coordinates for zooming
      const worldX = (pointerX - targetPosition.current.x) / oldScale
      const worldY = (pointerY - targetPosition.current.y) / oldScale

      // Update target scale and position
      targetScale.current = clampedNewScale
      targetPosition.current.x = pointerX - clampedNewScale * worldX
      targetPosition.current.y = pointerY - clampedNewScale * worldY
    }

    // Attach wheel event to the wrapper element.
    wrapperElement.addEventListener("wheel", onWheel as EventListener, {
      passive: false,
      signal
    })

    return () => {
      abortController.abort()
    }
  }, [wrapperRef, disabled, resetEasing])

  useTick(() => {
    if (!shouldAnimateRef.current || disabled || !containerRef.current) return

    const easing = easingRef.current // Easing factor for smooth transitions

    // Apply momentum if not actively panning.
    if (!isPanningRef.current) {
      const friction = 0.8 // Friction factor for momentum
      const velocityThreshold = 0.1 // Threshold to stop momentum

      if (Math.abs(velocityRef.current.x) > velocityThreshold || Math.abs(velocityRef.current.y) > velocityThreshold) {
        targetPosition.current.x += velocityRef.current.x // Apply momentum to position
        targetPosition.current.y += velocityRef.current.y
        velocityRef.current.x *= friction // Apply friction to velocity
        velocityRef.current.y *= friction
      } else {
        velocityRef.current = { x: 0, y: 0 } // Stop momentum
      }
    }

    // Smoothly interpolate current values toward target values.
    currentPosition.current.x = interpolate(currentPosition.current.x, targetPosition.current.x, easing)
    currentPosition.current.y = interpolate(currentPosition.current.y, targetPosition.current.y, easing)
    currentScale.current = interpolate(currentScale.current, targetScale.current, easing)

    // Clamp the scale.
    const clampedScale = Math.max(minScale, Math.min(maxScale, currentScale.current))

    // Apply position and scale to the container.
    containerRef.current.position.set(currentPosition.current.x, currentPosition.current.y)
    containerRef.current.scale.set(clampedScale)

    // Stop animating if position and scale are very close to target.
    const positionClose =
      Math.abs(targetPosition.current.x - currentPosition.current.x) < 1 &&
      Math.abs(targetPosition.current.y - currentPosition.current.y) < 1
    const scaleClose = Math.abs(targetScale.current - currentScale.current) < 0.001

    if (!isPanningRef.current && positionClose && scaleClose) {
      shouldAnimateRef.current = false
      resetEasing()

      // Update store when animation stops
      if (useStore) {
        setStoreZoomLevel(currentScale.current)
        setStorePanPosition(currentPosition.current)
      }
    }
  })

  // Smooth panning to a node instance (centers it), but only if it's outside the viewport
  function panToNode(node: DisplayObject) {
    const container = containerRef.current
    const wrapper = wrapperRef.current

    if (!container || !wrapper) return

    const bounds = node.getBounds()

    // Convert node bounds to screen coordinates relative to the container
    const nodeLocalBounds = container.toLocal({ x: bounds.x, y: bounds.y })
    const nodeLocalBoundsEnd = container.toLocal({
      x: bounds.x + bounds.width,
      y: bounds.y + bounds.height
    })

    const scale = targetScale.current
    const containerPos = currentPosition.current

    // Calculate screen coordinates
    const nodeScreenX = nodeLocalBounds.x * scale + containerPos.x
    const nodeScreenY = nodeLocalBounds.y * scale + containerPos.y
    const nodeScreenWidth = (nodeLocalBoundsEnd.x - nodeLocalBounds.x) * scale
    const nodeScreenHeight = (nodeLocalBoundsEnd.y - nodeLocalBounds.y) * scale

    // Use wrapper dimensions for accurate viewport checking
    const viewportWidth = wrapper.clientWidth
    const viewportHeight = wrapper.clientHeight

    // Add a small buffer to avoid edge cases with floating point precision
    const buffer = 0.01

    // Check if node is completely inside viewport (no panning needed)
    const isCompletelyInside =
      nodeScreenX >= buffer &&
      nodeScreenX + nodeScreenWidth <= viewportWidth - buffer &&
      nodeScreenY >= buffer &&
      nodeScreenY + nodeScreenHeight <= viewportHeight - buffer

    // Only pan if the node is not completely inside the viewport
    if (isCompletelyInside) return

    // Get node center in global space
    const nodeCenterGlobal = {
      x: bounds.x + bounds.width / 2,
      y: bounds.y + bounds.height / 2
    }

    // Convert to stage-local coordinates
    const nodeCenterLocal = container.toLocal(nodeCenterGlobal)

    // Calculate screen center
    const screenCenterX = wrapper.clientWidth / 2
    const screenCenterY = wrapper.clientHeight / 2

    // Calculate target position
    const x = screenCenterX - nodeCenterLocal.x * scale
    const y = screenCenterY - nodeCenterLocal.y * scale

    easingRef.current = PAN_TO_NODE_SPEED
    targetPosition.current = { x, y }
    velocityRef.current = { x: 0, y: 0 }
    shouldAnimateRef.current = true
  }

  return {
    panToNode
  }
}
